import { useState } from 'react'
import { db } from '../../firebase/config'
import { client } from '../../hooks/Client'
import firebase from '../../firebase/compat'
import { v4 as uuid } from 'uuid'
import { useFirestoreGeneralTwo } from '../../firebase/useFirestore'
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined'
import PushPinIcon from '@mui/icons-material/PushPin'
import Tooltip from '../common/Tooltip'
import snapshotAnalyticsState from '../../helpers/dashboardHelpers/dashboardCardFilters'

// AI-CONTEXT: See docs/patterns.md#data-analytics-pin-snapshot
// AI-CONTEXT: See docs/pitfalls.md#state-click-captured-firestore-snapshot
//   (this snapshot is intentional — the inverse of that pitfall)

/**
 * Pins an analytics source to the dashboard. Each click creates a NEW
 * `DashboardItems` doc — multiple pins of the same source are allowed; each
 * captures whatever filter / option state `getSnapshot` returns at click time.
 *
 * @param {object} props
 * @param {'effect'|'activity'|'indicator'|'output'|'outputIndicator'|'research'} props.type
 * @param {string} [props.effectId]
 * @param {string} [props.activityId]
 * @param {string} [props.indicatorId]
 * @param {string} [props.researchId]
 * @param {string} [props.outputId]
 * @param {string} props.label - Display label for the pinned card.
 * @param {boolean} [props.showLabel] - Render the labelled pill variant (vs icon-only).
 * @param {() => object} [props.getSnapshot] - Called at click time. Its return value is converted via `snapshotAnalyticsState` and spread into the new `DashboardItems` doc.
 */
const AddToDashboardButton = ({
    type,
    effectId,
    activityId,
    indicatorId,
    researchId,
    outputId,
    label,
    showLabel = false,
    getSnapshot,
}) => {
    // State
    const [saving, setSaving] = useState(false)

    // Resolve the stable SourceId used for "already added" detection
    const sourceId = outputId || indicatorId || researchId || activityId || effectId

    // Count existing matching cards. Multiple pins of the same source are
    // allowed (each holds its own captured filter snapshot), so this is just
    // a visual hint, not a gate.
    const existingItems = useFirestoreGeneralTwo('DashboardItems', 'Version', 2, 'SourceId', sourceId || '')
    const count = Array.isArray(existingItems) ? existingItems.length : 0
    const alreadyAdded = count > 0
    const showCountBadge = count >= 2

    const addHandler = async () => {
        if (saving || !sourceId) return

        setSaving(true)

        const countSnapshot = await db.collection('DashboardItems')
            .where('CompagnyID', '==', client)
            .where('Version', '==', 2)
            .get()

        const position = countSnapshot.size + 1
        const id = uuid()

        const filterSnapshot = getSnapshot ? snapshotAnalyticsState(getSnapshot()) : {}

        await db.collection('DashboardItems').doc().set({
            ID: id,
            CompagnyID: client,
            Compagny: client,
            Position: position,
            Version: 2,
            Timestamp: firebase.firestore.FieldValue.serverTimestamp(),
            Type: type,
            SourceId: sourceId,
            Label: label,
            // type-specific metadata
            ...(effectId && { EffectId: effectId }),
            ...(activityId && { ActivityId: activityId }),
            ...(indicatorId && { IndicatorId: indicatorId }),
            ...(researchId && { ResearchId: researchId }),
            ...(outputId && { OutputId: outputId }),
            // captured filter / option state at pin time
            ...filterSnapshot,
        })

        setSaving(false)
    }

    const pillTitle = alreadyAdded
        ? 'Op dashboard — klik om nogmaals toe te voegen met huidige filters'
        : 'Voeg toe aan dashboard'

    // ── Pill variant (icon + text label) ──
    if (showLabel) {
        const className = `add-to-dashboard-pill ${alreadyAdded ? 'add-to-dashboard-pill--added' : ''}`
        return (
            <button
                type='button'
                className={className}
                onClick={addHandler}
                disabled={saving}
                title={pillTitle}
            >
                {alreadyAdded
                    ? <PushPinIcon className='add-to-dashboard-pill-icon' />
                    : <PushPinOutlinedIcon className='add-to-dashboard-pill-icon' />
                }
                <span>{alreadyAdded ? 'Op dashboard' : 'Aan dashboard'}</span>
                {showCountBadge && (
                    <span className='add-to-dashboard-pill-count'>{count}</span>
                )}
            </button>
        )
    }

    // ── Icon-only variant ──
    const tooltipContent = alreadyAdded
        ? (count >= 2 ? `${count}× op dashboard — klik om nogmaals toe te voegen` : 'Op dashboard — klik om nogmaals toe te voegen')
        : 'Voeg toe aan dashboard'

    const IconComponent = alreadyAdded ? PushPinIcon : PushPinOutlinedIcon
    const iconClass = `add-to-dashboard-icon ${alreadyAdded ? 'add-to-dashboard-icon--added' : ''} ${saving ? 'add-to-dashboard-icon--saving' : ''}`

    return (
        <Tooltip content={tooltipContent} top='30px'>
            <span className='add-to-dashboard-icon-wrapper' data-copy-hide>
                <IconComponent
                    className={iconClass}
                    onClick={addHandler}
                />
                {showCountBadge && (
                    <span className='add-to-dashboard-icon-count'>{count}</span>
                )}
            </span>
        </Tooltip>
    )
}

export default AddToDashboardButton
